import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import authService from "../../services/authService";

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dropdownRef = useRef(null);
  const [userInitials, setUserInitials] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  const dashboardLink =
    isLoggedIn && location.pathname === "/research"
      ? { to: "/research/data-view", label: "Data View" }
      : isLoggedIn && location.pathname !== "/research"
        ? { to: "/research", label: "Dashboard" }
        : null;
  const mobileNavigationLinks = dashboardLink ? [dashboardLink] : [];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get user initials from localStorage
  useEffect(() => {
    const user = localStorage.getItem("user");
    setIsLoggedIn(!!user);
    if (user) {
      try {
        const userData = JSON.parse(user);
        const firstInitial = userData.first_name
          ? userData.first_name.charAt(0).toUpperCase()
          : "";
        const lastInitial = userData.last_name
          ? userData.last_name.charAt(0).toUpperCase()
          : "";
        setUserInitials(firstInitial + lastInitial || "");
      } catch (e) {
        console.error("Error parsing user data:", e);
      }
    }
  }, []);

  const handleLogout = async () => {
    if (isLoggingOut) {
      return;
    }

    setIsLoggingOut(true);
    setOpenMenu(false);
    setIsLoggedIn(false);
    setUserInitials("");

    authService.logout().catch((err) => {
      console.error("Logout error:", err);
    });

    navigate('/login', { replace: true });
  };

  const handleMenuClose = () => {
    setOpenMenu(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-700/20 bg-[#111318]/95 backdrop-blur-xl">
      <div className="mx-auto flex max-w-[1440px] gap-4 px-4 py-4 sm:px-6 flex-row items-center justify-between lg:px-10">
        <div className="flex min-w-0 items-center gap-3.5">
          <Link
            to="/research"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-blue-500 to-blue-700"
          >
            <span className="sr-only">Limbus Intelligence</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="12"
                r="8"
                stroke="white"
                strokeWidth="1.5"
                fill="none"
              />
              <circle
                cx="12"
                cy="12"
                r="4"
                stroke="white"
                strokeWidth="1.5"
                fill="none"
              />
              <circle cx="12" cy="12" r="1.5" fill="white" />
            </svg>
          </Link>
          <div className="min-w-0">
            <Link
              to="/research"
              className="block text-[15px] font-semibold leading-tight tracking-[-0.01em] text-slate-50 sm:text-[17px]"
            >
              Limbus Intelligence
            </Link>
            <div className="text-[11px] font-medium leading-tight text-slate-500">
              Ophthalmic Industry Database
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 sm:justify-end sm:gap-5">
          {dashboardLink && (
            <Link
              to={dashboardLink.to}
              className="hidden items-center rounded-md bg-blue-500/10 px-3 py-1.5 text-xs font-medium text-blue-500 sm:inline-flex"
            >
              {dashboardLink.label}
            </Link>
          )}

          {!!userInitials && (
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                className="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-gradient-to-br from-slate-800 to-slate-700 text-[13px] font-semibold text-slate-400"
                onClick={() => setOpenMenu((prev) => !prev)}
              >
                {userInitials}
              </button>

              {openMenu && (
                <div className="absolute right-0 top-11 z-10 w-[min(18rem,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-slate-800 bg-slate-900 shadow-[0_8px_20px_rgba(0,0,0,0.35)] sm:w-56">
                  <ul className="py-2">
                    {mobileNavigationLinks.map((item) => (
                      <li key={item.to} className="sm:hidden">
                        <Link
                          to={item.to}
                          onClick={handleMenuClose}
                          className={`block px-3.5 py-2.5 text-sm transition-colors hover:bg-slate-800 ${
                            location.pathname === item.to
                              ? "bg-slate-800/70 text-blue-400"
                              : "text-slate-200"
                          }`}
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                    {mobileNavigationLinks.length > 0 && (
                      <li className="mx-3.5 my-2 border-t border-slate-800 sm:hidden" />
                    )}
                    <li>
                      <Link
                        to="/profile"
                        onClick={handleMenuClose}
                        className="block px-3.5 py-2.5 text-sm text-slate-200 transition-colors hover:bg-slate-800"
                      >
                        My Profile
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/change-password"
                        onClick={handleMenuClose}
                        className="block px-3.5 py-2.5 text-sm text-slate-200 transition-colors hover:bg-slate-800"
                      >
                        Change Password
                      </Link>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={handleLogout}
                        disabled={isLoggingOut}
                        className="block w-full cursor-pointer px-3.5 py-2.5 text-left text-sm text-slate-300 transition-colors hover:bg-red-500/10 hover:text-red-400"
                      >
                        {isLoggingOut ? "Logging out..." : "Logout"}
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
